import type { IntehrLocale } from "../blockly/i18n/custom_msg.ts";

export interface SheetsChrome {
  tab: string;
  add: string;
  addDecision: string;
  addInput: string;
  addOutput: string;
  outputType: string;
  moveLeft: string;
  moveRight: string;
  columnKind: string;
  kindString: string;
  kindNumber: string;
  kindBoolean: string;
  kindStringPattern: string;
  rename: string;
  remove: string;
  deleteColumn: string;
  deletedColumn: string;
  dontCare: string;
  columnMenu: string;
  importCsv: string;
  exportCsv: string;
  fullscreen: string;
  exitFullscreen: string;
  empty: string;
  namePrompt: string;
  decisionNamePrompt: string;
  hitPolicy: string;
  hitPolicyAbout: string;
  hitPolicyFirst: string;
  hitPolicyUnique: string;
  hitPolicyCollect: string;
  hitPolicyDocsLabel: string;
  kindSheet: string;
  kindDecision: string;
  selectSheet: string;
  selectTable: string;
  snippetLint: string;
  addDefaultRow: string;
  collectDedupe: string;
}

/** Longer hit-policy write-up linked from the Sheets (i) control. */
export const HIT_POLICY_DOCS_HREF =
  "https://github.com/regionstockholm/intehrgrator/blob/main/docs/tutorial/sheets-and-decision-tables.md#hit-policy";

export function sheetsFormat(template: string, name: string): string {
  return template.replaceAll("{name}", name);
}

const CHROME: Record<IntehrLocale, SheetsChrome> = {
  en: {
    tab: "Tables & Sheets",
    add: "Add sheet",
    addDecision: "Add decision table",
    addInput: "Add input/condition",
    addOutput: "Add output",
    outputType: "Output type",
    moveLeft: "Move left",
    moveRight: "Move right",
    columnKind: "column kind",
    kindString: "string",
    kindNumber: "number",
    kindBoolean: "boolean",
    kindStringPattern: "string pattern",
    rename: "Rename",
    remove: "Delete",
    deleteColumn: "Delete {name}",
    deletedColumn: "Deleted column {name}. Undo can restore it.",
    dontCare: "Don't care (—)",
    columnMenu: "{name} column menu",
    importCsv: "Import CSV",
    exportCsv: "Export CSV",
    fullscreen: "Fullscreen",
    exitFullscreen: "Exit fullscreen",
    empty: "No sheets yet. Add a sheet, a decision table, or import a CSV.",
    namePrompt: "Sheet name",
    decisionNamePrompt: "Decision table name",
    hitPolicy: "Hit policy",
    hitPolicyAbout: "About hit policy",
    hitPolicyFirst: "Use the first matching row. Later matches are ignored.",
    hitPolicyUnique: "Only one row may match. More than one match is an error.",
    hitPolicyCollect: "Join every matching row’s output, in row order. Blank cells are skipped.",
    hitPolicyDocsLabel: "Sheets and decision tables",
    kindSheet: "Sheet",
    kindDecision: "Decision table",
    selectSheet: "-- select sheet --",
    selectTable: "-- select table --",
    snippetLint: "Snippet cells (VMS-Mustache)",
    addDefaultRow: "Add default row",
    collectDedupe: "COLLECT dedupe",
  },
  sv: {
    tab: "Tabeller & kalkylblad",
    add: "Nytt blad",
    addDecision: "Ny beslutstabell",
    addInput: "Lägg till indata/villkor",
    addOutput: "Ny utdata",
    outputType: "Utdatatyp",
    moveLeft: "Flytta vänster",
    moveRight: "Flytta höger",
    columnKind: "kolumnslag",
    kindString: "sträng",
    kindNumber: "tal",
    kindBoolean: "boolesk",
    kindStringPattern: "strängmönster",
    rename: "Byt namn",
    remove: "Ta bort",
    deleteColumn: "Ta bort {name}",
    deletedColumn: "Kolumnen {name} togs bort. Ångra kan återställa den.",
    dontCare: "Spelar ingen roll (—)",
    columnMenu: "Kolumnmeny för {name}",
    importCsv: "Importera CSV",
    exportCsv: "Exportera CSV",
    fullscreen: "Helskärm",
    exitFullscreen: "Lämna helskärm",
    empty: "Inga blad ännu. Lägg till blad, beslutstabell eller importera CSV.",
    namePrompt: "Bladnamn",
    decisionNamePrompt: "Namn på beslutstabell",
    hitPolicy: "Träffpolicy",
    hitPolicyAbout: "Om träffpolicy",
    hitPolicyFirst: "Använd första matchande raden. Senare träffar ignoreras.",
    hitPolicyUnique: "Bara en rad får matcha. Fler än en träff är ett fel.",
    hitPolicyCollect: "Slå ihop varje matchande rads utdata, i radordning. Tomma celler hoppas över.",
    hitPolicyDocsLabel: "Blad och beslutstabeller",
    kindSheet: "Blad",
    kindDecision: "Beslutstabell",
    selectSheet: "-- välj kalkylblad --",
    selectTable: "-- välj tabell --",
    snippetLint: "Snippet-celler (VMS-Mustache)",
    addDefaultRow: "Lägg till standardrad",
    collectDedupe: "COLLECT-deduplicering",
  },
  de: {
    tab: "Tabellen & Blätter",
    add: "Blatt hinzufügen",
    addDecision: "Entscheidungstabelle",
    addInput: "Eingabe/Bedingung",
    addOutput: "Ausgabe",
    outputType: "Ausgabetyp",
    moveLeft: "Nach links",
    moveRight: "Nach rechts",
    columnKind: "Spaltenart",
    kindString: "Zeichenkette",
    kindNumber: "Zahl",
    kindBoolean: "Boolesch",
    kindStringPattern: "Zeichenkettenmuster",
    rename: "Umbenennen",
    remove: "Löschen",
    deleteColumn: "{name} löschen",
    deletedColumn: "Spalte {name} wurde gelöscht. Rückgängig stellt sie wieder her.",
    dontCare: "Egal (—)",
    columnMenu: "Spaltenmenü {name}",
    importCsv: "CSV importieren",
    exportCsv: "CSV exportieren",
    fullscreen: "Vollbild",
    exitFullscreen: "Vollbild beenden",
    empty: "Noch keine Blätter. Blatt, Entscheidungstabelle oder CSV.",
    namePrompt: "Blattname",
    decisionNamePrompt: "Name der Entscheidungstabelle",
    hitPolicy: "Trefferpolitik",
    hitPolicyAbout: "Über Trefferpolitik",
    hitPolicyFirst: "Die erste passende Zeile gilt. Spätere Treffer werden ignoriert.",
    hitPolicyUnique: "Nur eine Zeile darf passen. Mehr als ein Treffer ist ein Fehler.",
    hitPolicyCollect: "Alle passenden Ausgaben aneinanderfügen, in Zeilenreihenfolge. Leere Zellen entfallen.",
    hitPolicyDocsLabel: "Blätter und Entscheidungstabellen",
    kindSheet: "Blatt",
    kindDecision: "Entscheidungstabelle",
    selectSheet: "-- Blatt wählen --",
    selectTable: "-- Tabelle wählen --",
    snippetLint: "Snippet-Zellen (VMS-Mustache)",
    addDefaultRow: "Standardzeile hinzufügen",
    collectDedupe: "COLLECT-Deduplizierung",
  },
  es: {
    tab: "Tablas y hojas",
    add: "Añadir hoja",
    addDecision: "Tabla de decisión",
    addInput: "Añadir entrada/condición",
    addOutput: "Añadir salida",
    outputType: "Tipo de salida",
    moveLeft: "Mover a la izquierda",
    moveRight: "Mover a la derecha",
    columnKind: "tipo de columna",
    kindString: "cadena",
    kindNumber: "número",
    kindBoolean: "booleano",
    kindStringPattern: "patrón de cadena",
    rename: "Renombrar",
    remove: "Eliminar",
    deleteColumn: "Eliminar {name}",
    deletedColumn: "Se eliminó la columna {name}. Deshacer puede restaurarla.",
    dontCare: "Indiferente (—)",
    columnMenu: "Menú de columna {name}",
    importCsv: "Importar CSV",
    exportCsv: "Exportar CSV",
    fullscreen: "Pantalla completa",
    exitFullscreen: "Salir de pantalla completa",
    empty: "Aún no hay hojas. Añada hoja, tabla de decisión o CSV.",
    namePrompt: "Nombre de la hoja",
    decisionNamePrompt: "Nombre de la tabla de decisión",
    hitPolicy: "Política de acierto",
    hitPolicyAbout: "Sobre la política de acierto",
    hitPolicyFirst: "Se usa la primera fila que coincide. Las coincidencias posteriores se ignoran.",
    hitPolicyUnique: "Solo una fila puede coincidir. Más de una coincidencia es un error.",
    hitPolicyCollect: "Une la salida de cada fila coincidente, en orden. Las celdas vacías se omiten.",
    hitPolicyDocsLabel: "Hojas y tablas de decisión",
    kindSheet: "Hoja",
    kindDecision: "Tabla de decisión",
    selectSheet: "-- seleccionar hoja --",
    selectTable: "-- seleccionar tabla --",
    snippetLint: "Celdas snippet (VMS-Mustache)",
    addDefaultRow: "Añadir fila por defecto",
    collectDedupe: "COLLECT sin duplicados",
  },
  ca: {
    tab: "Taules i fulls",
    add: "Afegeix full",
    addDecision: "Taula de decisió",
    addInput: "Afegeix entrada/condició",
    addOutput: "Afegeix sortida",
    outputType: "Tipus de sortida",
    moveLeft: "Mou a l'esquerra",
    moveRight: "Mou a la dreta",
    columnKind: "tipus de columna",
    kindString: "cadena",
    kindNumber: "nombre",
    kindBoolean: "booleà",
    kindStringPattern: "patró de cadena",
    rename: "Canvia el nom",
    remove: "Suprimeix",
    deleteColumn: "Suprimeix {name}",
    deletedColumn: "S'ha suprimit la columna {name}. Desfés la pot restaurar.",
    dontCare: "Indiferent (—)",
    columnMenu: "Menú de columna {name}",
    importCsv: "Importa CSV",
    exportCsv: "Exporta CSV",
    fullscreen: "Pantalla completa",
    exitFullscreen: "Surt de pantalla completa",
    empty: "Encara no hi ha fulls. Afegiu full, taula de decisió o CSV.",
    namePrompt: "Nom del full",
    decisionNamePrompt: "Nom de la taula de decisió",
    hitPolicy: "Política d'encert",
    hitPolicyAbout: "Sobre la política d'encert",
    hitPolicyFirst: "S'usa la primera fila que coincideix. Les coincidències posteriors s'ignoren.",
    hitPolicyUnique: "Només una fila pot coincidir. Més d'una coincidència és un error.",
    hitPolicyCollect: "Uneix la sortida de cada fila coincident, en ordre. Les cel·les buides s'ometen.",
    hitPolicyDocsLabel: "Fulls i taules de decisió",
    kindSheet: "Full",
    kindDecision: "Taula de decisió",
    selectSheet: "-- selecciona un full --",
    selectTable: "-- selecciona una taula --",
    snippetLint: "Cel·les snippet (VMS-Mustache)",
    addDefaultRow: "Afegeix fila per defecte",
    collectDedupe: "COLLECT sense duplicats",
  },
  fr: {
    tab: "Tableaux et feuilles",
    add: "Ajouter une feuille",
    addDecision: "Table de décision",
    addInput: "Ajouter entrée/condition",
    addOutput: "Ajouter une sortie",
    outputType: "Type de sortie",
    moveLeft: "Déplacer à gauche",
    moveRight: "Déplacer à droite",
    columnKind: "sorte de colonne",
    kindString: "chaîne",
    kindNumber: "nombre",
    kindBoolean: "booléen",
    kindStringPattern: "motif de chaîne",
    rename: "Renommer",
    remove: "Supprimer",
    deleteColumn: "Supprimer {name}",
    deletedColumn: "Colonne {name} supprimée. Annuler peut la restaurer.",
    dontCare: "Indifférent (—)",
    columnMenu: "Menu de colonne {name}",
    importCsv: "Importer CSV",
    exportCsv: "Exporter CSV",
    fullscreen: "Plein écran",
    exitFullscreen: "Quitter le plein écran",
    empty: "Pas encore de feuilles. Ajoutez feuille, table de décision ou CSV.",
    namePrompt: "Nom de la feuille",
    decisionNamePrompt: "Nom de la table de décision",
    hitPolicy: "Politique de hit",
    hitPolicyAbout: "À propos de la politique de hit",
    hitPolicyFirst: "La première ligne correspondante est utilisée. Les suivantes sont ignorées.",
    hitPolicyUnique: "Une seule ligne peut correspondre. Plusieurs correspondances sont une erreur.",
    hitPolicyCollect: "Concatène la sortie de chaque ligne correspondante, dans l'ordre. Les cellules vides sont omises.",
    hitPolicyDocsLabel: "Feuilles et tables de décision",
    kindSheet: "Feuille",
    kindDecision: "Table de décision",
    selectSheet: "-- choisir une feuille --",
    selectTable: "-- choisir une table --",
    snippetLint: "Cellules snippet (VMS-Mustache)",
    addDefaultRow: "Ajouter une ligne par défaut",
    collectDedupe: "COLLECT sans doublons",
  },
};

/** jspreadsheet.setDictionary keys are the original English UI strings. */
const JSS_DICTIONARY: Record<IntehrLocale, Record<string, string>> = {
  en: {},
  sv: {
    "Search": "Sök",
    "Cut": "Klipp ut",
    "Copy": "Kopiera",
    "Paste": "Klistra in",
    "Insert a new column before": "Infoga kolumn före",
    "Insert a new column after": "Infoga kolumn efter",
    "Delete selected columns": "Ta bort valda kolumner",
    "Rename this column": "Byt namn på kolumn",
    "Insert a new row before": "Infoga rad före",
    "Insert a new row after": "Infoga rad efter",
    "Delete selected rows": "Ta bort valda rader",
    "Save as": "Spara som",
    "About": "Om",
    "Are you sure?": "Är du säker?",
    "No records found": "Inga poster hittades",
    "Order ascending": "Sortera stigande",
    "Order descending": "Sortera fallande",
  },
  de: {
    "Search": "Suchen",
    "Cut": "Ausschneiden",
    "Copy": "Kopieren",
    "Paste": "Einfügen",
    "Insert a new column before": "Spalte davor einfügen",
    "Insert a new column after": "Spalte danach einfügen",
    "Delete selected columns": "Ausgewählte Spalten löschen",
    "Rename this column": "Spalte umbenennen",
    "Insert a new row before": "Zeile davor einfügen",
    "Insert a new row after": "Zeile danach einfügen",
    "Delete selected rows": "Ausgewählte Zeilen löschen",
    "Save as": "Speichern unter",
    "About": "Über",
    "Are you sure?": "Sind Sie sicher?",
    "No records found": "Keine Einträge gefunden",
    "Order ascending": "Aufsteigend sortieren",
    "Order descending": "Absteigend sortieren",
  },
  es: {
    "Search": "Buscar",
    "Cut": "Cortar",
    "Copy": "Copiar",
    "Paste": "Pegar",
    "Insert a new column before": "Insertar columna antes",
    "Insert a new column after": "Insertar columna después",
    "Delete selected columns": "Eliminar columnas seleccionadas",
    "Rename this column": "Renombrar columna",
    "Insert a new row before": "Insertar fila antes",
    "Insert a new row after": "Insertar fila después",
    "Delete selected rows": "Eliminar filas seleccionadas",
    "Save as": "Guardar como",
    "About": "Acerca de",
    "Are you sure?": "¿Está seguro?",
    "No records found": "No se encontraron registros",
    "Order ascending": "Ordenar ascendente",
    "Order descending": "Ordenar descendente",
  },
  ca: {
    "Search": "Cerca",
    "Cut": "Retalla",
    "Copy": "Copia",
    "Paste": "Enganxa",
    "Insert a new column before": "Insereix columna abans",
    "Insert a new column after": "Insereix columna després",
    "Delete selected columns": "Suprimeix les columnes seleccionades",
    "Rename this column": "Canvia el nom de la columna",
    "Insert a new row before": "Insereix fila abans",
    "Insert a new row after": "Insereix fila després",
    "Delete selected rows": "Suprimeix les files seleccionades",
    "Save as": "Anomena i desa",
    "About": "Quant a",
    "Are you sure?": "Segur?",
    "No records found": "No s'han trobat registres",
    "Order ascending": "Ordena ascendent",
    "Order descending": "Ordena descendent",
  },
  fr: {
    "Search": "Rechercher",
    "Cut": "Couper",
    "Copy": "Copier",
    "Paste": "Coller",
    "Insert a new column before": "Insérer une colonne avant",
    "Insert a new column after": "Insérer une colonne après",
    "Delete selected columns": "Supprimer les colonnes sélectionnées",
    "Rename this column": "Renommer la colonne",
    "Insert a new row before": "Insérer une ligne avant",
    "Insert a new row after": "Insérer une ligne après",
    "Delete selected rows": "Supprimer les lignes sélectionnées",
    "Save as": "Enregistrer sous",
    "About": "À propos",
    "Are you sure?": "Êtes-vous sûr ?",
    "No records found": "Aucun enregistrement",
    "Order ascending": "Tri croissant",
    "Order descending": "Tri décroissant",
  },
};

export function sheetsChrome(locale: string): SheetsChrome {
  return CHROME[(locale in CHROME ? locale : "en") as IntehrLocale];
}

export function jspreadsheetDictionary(locale: string): Record<string, string> {
  return JSS_DICTIONARY[(locale in JSS_DICTIONARY ? locale : "en") as IntehrLocale];
}
